/**
 * Company Search — ambiguous-match picker (sealed state 4).
 *
 * Renders lookup_company's candidates[] envelope: each candidate is a
 * full-width card showing the name and the free-text disambiguation_context
 * the contract provides (structured city/state/country is not available, so it
 * is not fabricated). The sealed escape hatch — "None of these match / Refine
 * your search" — clears the query so the user can search again.
 */

import type { LookupCandidateView } from "../types";
import { IconChevron } from "./icons";

export function AmbiguousPicker({
  candidates,
  selectedId,
  onSelect,
  onRefine,
}: {
  candidates: LookupCandidateView[];
  selectedId: string | null;
  onSelect: (organizationId: string, displayName: string | null) => void;
  onRefine: () => void;
}): JSX.Element {
  return (
    <div className="sxco-picker">
      <div className="sxco-picker-head">
        <h2 className="sxco-picker-title">We found multiple matches</h2>
        <p className="sxco-picker-sub">Please select the company you’re looking for.</p>
      </div>
      <ul className="sxco-result-list">
        {candidates.map((c) => {
          const active = c.organizationId === selectedId;
          return (
            <li key={c.organizationId}>
              <button
                type="button"
                className={`sxco-result${active ? " sxco-result--active" : ""}`}
                aria-pressed={active}
                onClick={() => onSelect(c.organizationId, c.displayName)}
              >
                <span className="sxco-result-text">
                  <span className="sxco-result-name">{c.displayName ?? "—"}</span>
                  {c.context && <span className="sxco-result-ctx">{c.context}</span>}
                </span>
                <span className="sxco-result-chev" aria-hidden="true">
                  <IconChevron />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="sxco-picker-foot">
        <span className="sxco-muted">None of these match?</span>
        <button type="button" className="sxco-linkbtn" onClick={onRefine}>
          Refine your search
        </button>
      </div>
    </div>
  );
}
